import React, { useState, useEffect } from 'react';
import { getTrades } from '../../api/client';
import api from '../../api/client';

// ── TAG CONFIG ──
const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  FVG:          { bg: 'rgba(64,144,240,.12)',  color: '#4090f0' },
  OB:           { bg: 'rgba(144,96,240,.12)',  color: '#9060f0' },
  BOS:          { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  CHoCH:        { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  Support:      { bg: 'rgba(240,160,0,.12)',   color: '#F0A500' },
  Resistance:   { bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Breakout:     { bg: 'rgba(0,201,167,.12)',   color: '#00c9a7' },
  Range:        { bg: 'rgba(85,96,128,.12)',   color: '#556080' },
  Trend:        { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  Reversal:     { bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Disciplined:  { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  FOMO:         { bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Revenge:      { bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Hesitated:    { bg: 'rgba(240,160,0,.12)',   color: '#F0A500' },
  Overconfident:{ bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Patient:      { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  Asia:         { bg: 'rgba(64,144,240,.12)',  color: '#4090f0' },
  London:       { bg: 'rgba(240,160,0,.12)',   color: '#F0A500' },
  US:           { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  'TP Hit':     { bg: 'rgba(0,201,122,.12)',   color: '#00C97A' },
  'SL Hit':     { bg: 'rgba(240,64,96,.12)',   color: '#f04060' },
  Trail:        { bg: 'rgba(240,160,0,.12)',   color: '#F0A500' },
  'Early Exit': { bg: 'rgba(240,160,0,.12)',   color: '#F0A500' },
  'Manual Close':{ bg: 'rgba(85,96,128,.12)',  color: '#556080' },
};

const SETUP_TAGS     = ['FVG','OB','BOS','CHoCH','Support','Resistance','Pullback','Trendline Touch','Trendline Break'];
const MARKET_TAGS    = ['Trending','Ranging','Breakout','Reversal'];

function Tag({ label, onRemove }: { label: string; onRemove?: () => void }) {
  const c = TAG_COLORS[label] || { bg: 'rgba(85,96,128,.12)', color: '#556080' };
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:3,
      padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700,
      background:c.bg, color:c.color, letterSpacing:'.04em',
    }}>
      {label}
      {onRemove && (
        <span onClick={e => { e.stopPropagation(); onRemove(); }}
          style={{ cursor:'pointer', opacity:.6, fontSize:12, lineHeight:1 }}>×</span>
      )}
    </span>
  );
}

function TagPicker({ selected, onChange, tradeId }: {
  selected: string[];
  onChange: (t: string[]) => void;
  tradeId: string;
}) {
  const [customSetup,   setCustomSetup]   = useState<string[]>([]);
  const [customMarket,  setCustomMarket]  = useState<string[]>([]);
  const [newSetup,      setNewSetup]      = useState('');
  const [newMarket,     setNewMarket]     = useState('');
  const [customTag,     setCustomTag]     = useState('');

  useEffect(() => {
    import('../../api/client').then(({ default: api }) => {
      api.get('/api/v1/trades/custom-tags')
        .then((r:any) => {
          const data = r.data || [];
          setCustomSetup( data.filter((x:any) => x.category==='setup').map((x:any) => x.tag_name));
          setCustomMarket(data.filter((x:any) => x.category==='market_condition').map((x:any) => x.tag_name));
        }).catch(() => {});
    });
  }, []);

  const toggle = (t: string) =>
    onChange(selected.includes(t) ? selected.filter(x => x !== t) : [...selected, t]);

  const saveCustom = (category: string, tag: string, setter: (v:string)=>void, listSetter: (f:(p:string[])=>string[])=>void) => {
    const v = tag.trim();
    if (!v) return;
    import('../../api/client').then(({ default: api }) => {
      api.post('/api/v1/trades/custom-tags', { category, tag_name: v })
        .then(() => {
          listSetter(prev => prev.includes(v) ? prev : [...prev, v]);
          if (!selected.includes(v)) onChange([...selected, v]);
          setter('');
        }).catch(() => {});
    });
  };

  const addCustomTag = () => {
    const v = customTag.trim();
    if (v && !selected.includes(v)) { onChange([...selected, v]); setCustomTag(''); }
  };

  const TagSection = ({ title, defaultTags, customTags, color, newVal, setNew, category, setList }: any) => (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:9, color:'#3a4560', textTransform:'uppercase' as const,
        letterSpacing:'.1em', marginBottom:5, fontWeight:600 }}>{title}</div>
      <div style={{ display:'flex', flexWrap:'wrap' as const, gap:5, marginBottom:6 }}>
        {[...defaultTags, ...customTags].map((t:string) => {
          const active = selected.includes(t);
          return (
            <span key={t} onClick={() => toggle(t)} style={{
              padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:700,
              cursor:'pointer', userSelect:'none' as const,
              border:`1px solid ${active ? color : '#252d42'}`,
              color: active ? color : '#556080',
              background: active ? `${color}15` : 'transparent',
              transition:'all .15s',
            }}>{t}</span>
          );
        })}
      </div>
      <div style={{ display:'flex', gap:5 }}>
        <input value={newVal} onChange={e => setNew(e.target.value)}
          onKeyDown={(e:any) => e.key==='Enter' && saveCustom(category, newVal, setNew, setList)}
          placeholder={`Add custom ${title.toLowerCase()}...`}
          style={{ flex:1, padding:'4px 8px', background:'#111626',
            border:'1px solid #1a1f30', borderRadius:4,
            color:'#E8ECF4', fontSize:10, fontFamily:'inherit', outline:'none' }}/>
        <button onClick={() => saveCustom(category, newVal, setNew, setList)} style={{
          padding:'4px 10px', background:'#1a1f30', border:'none',
          borderRadius:4, color:'#E8ECF4', fontSize:10, cursor:'pointer' }}>+</button>
      </div>
    </div>
  );

  return (
    <div>
      <TagSection title="Setup Tags"       defaultTags={SETUP_TAGS}  customTags={customSetup}
        color="#4090f0" newVal={newSetup}  setNew={setNewSetup}
        category="setup"            setList={(f:any) => setCustomSetup(f)} />
      <TagSection title="Market Condition" defaultTags={MARKET_TAGS} customTags={customMarket}
        color="#F0A500" newVal={newMarket} setNew={setNewMarket}
        category="market_condition" setList={(f:any) => setCustomMarket(f)} />
      <div style={{ marginTop:4 }}>
        <div style={{ fontSize:9, color:'#3a4560', textTransform:'uppercase' as const,
          letterSpacing:'.1em', marginBottom:5, fontWeight:600 }}>Custom Tags</div>
        <div style={{ display:'flex', gap:5 }}>
          <input value={customTag} onChange={e => setCustomTag(e.target.value)}
            onKeyDown={(e:any) => e.key==='Enter' && addCustomTag()}
            placeholder="Add custom tag..."
            style={{ flex:1, padding:'4px 8px', background:'#111626',
              border:'1px solid #1a1f30', borderRadius:4,
              color:'#E8ECF4', fontSize:10, fontFamily:'inherit', outline:'none' }}/>
          <button onClick={addCustomTag} style={{
            padding:'4px 10px', background:'#1a1f30', border:'none',
            borderRadius:4, color:'#E8ECF4', fontSize:10, cursor:'pointer' }}>+</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: any) {
  return (
    <div style={{ background:'#0c0f1a', border:'1px solid #1a1f30',
      borderRadius:8, padding:'16px 20px' }}>
      <div style={{ fontSize:10, color:'#556080', textTransform:'uppercase',
        letterSpacing:'.08em', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color: color||'#E8ECF4',
        fontFamily:'Georgia,serif' }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'#556080', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function formatDur(open: string, close: string) {
  const ms = new Date(close).getTime() - new Date(open).getTime();
  const m  = Math.floor(ms/60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h ${m%60}m`;
  return `${Math.floor(h/24)}d ${h%24}h`;
}

function TradeRow({ trade, onUpdate }: { trade: any; onUpdate: (t: any) => void }) {
  const [open,      setOpen]      = useState(false);
  const SYSTEM_TAGS  = ['Asia','London','US','London/US Overlap',
    'No Risk','Balanced Risk','Elevated Risk','Aggressive Risk',
    'Clarity','Desperate','Reckless','Forcing Trade','Gamble',
    'Calm','Disciplined','Fear','Panic','Lucky','Patient','Strategic',
    'Impatient','Greedy','Conservative','TP Hit','SL Hit','Trail','Manual Close',
    'News Risk'];
  // Only editable tags (setup + market + custom) — exclude system computed ones
  const [tags, setTags] = useState<string[]>(
    (trade.tags || []).filter((t:string) => !SYSTEM_TAGS.includes(t))
  );
  const [notes,     setNotes]     = useState(trade.notes || '');
  const [saving,    setSaving]    = useState(false);
  const [tab,       setTab]       = useState<'overview'|'charts'|'notes'>('overview');
  const [screenshots, setScreenshots] = useState<any>(null);
  const [loadingSS,   setLoadingSS]   = useState(false);

  const win  = (trade.execution_outcome||'').startsWith('WIN');
  const pnl  = parseFloat(trade.net_pnl || 0);
  const dec  = (trade.entry_price||0) > 100 ? 2 : 5;

  const save = async () => {
    setSaving(true);
    try {
      // Preserve system tags, merge with user editable tags
      const systemTagsOnTrade = (trade.tags || []).filter((t:string) => SYSTEM_TAGS.includes(t));
      const mergedTags = Array.from(new Set([...systemTagsOnTrade, ...tags]));
      await api.put(`/api/v1/trades/${trade.id}/journal`, { notes, tags: mergedTags });
      onUpdate({ ...trade, notes, tags: mergedTags });
    } catch(e) {}
    setSaving(false);
  };


  const exitQ    = trade.exit_quality;
  const exitColor = exitQ === 'PERFECT' ? '#00C97A' : exitQ === 'EARLY' ? '#F0A500' : '#4090f0';

  return (
    <>
      {/* COLLAPSED ROW */}
      <tr onClick={() => setOpen(!open)}
        style={{ cursor:'pointer', borderBottom:'1px solid #111626',
          background: open ? '#0d1020' : 'transparent', transition:'background .15s' }}>
        <td style={{ padding:'12px 16px', color:'#556080', fontSize:11 }}>
          {trade.close_time ? new Date(trade.close_time).toLocaleDateString('en-AU',
            { day:'2-digit', month:'short' }) : '—'}
          <div style={{ fontSize:10, color:'#3a4560', marginTop:2 }}>
            {trade.close_time ? new Date(trade.close_time).toLocaleTimeString('en-AU',
              { hour:'2-digit', minute:'2-digit' }) : ''}
          </div>
        </td>
        <td style={{ padding:'12px 16px' }}>
          <div style={{ fontWeight:700, color:'#E8ECF4', fontSize:13 }}>{trade.symbol}</div>
          <div style={{ fontSize:10, color:'#556080', marginTop:2 }}>
            {trade.scanner === 'S1' || trade.scanner === 'S2' ? 'Algo' : 'Manual'}
          </div>
        </td>
        <td style={{ padding:'12px 16px' }}>
          <span style={{
            padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:700,
            background: trade.bias==='BUY' ? 'rgba(0,201,122,.12)' : 'rgba(240,64,96,.12)',
            color: trade.bias==='BUY' ? '#00C97A' : '#f04060',
          }}>{trade.bias}</span>
        </td>
        <td style={{ padding:'12px 16px', fontSize:12, color:'#8899b4' }}>{trade.lot}</td>
        <td style={{ padding:'12px 16px', fontSize:12, color:'#8899b4' }}>
          {parseFloat(trade.entry_price||0).toFixed(dec)}
        </td>
        <td style={{ padding:'12px 16px', fontSize:12, color:'#8899b4' }}>
          {trade.close_price ? parseFloat(trade.close_price).toFixed(dec) : '—'}
        </td>
        <td style={{ padding:'12px 16px', fontSize:12, color:'#556080' }}>
          {trade.open_time && trade.close_time ? formatDur(trade.open_time, trade.close_time) : '—'}
        </td>
        <td style={{ padding:'12px 16px' }}>
          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
            {(tags||[]).slice(0,3).map((t: string) => <Tag key={t} label={t} />)}
            {(tags||[]).length > 3 && (
              <span style={{ fontSize:10, color:'#556080' }}>+{(tags||[]).length-3}</span>
            )}
          </div>
        </td>
        <td style={{ padding:'12px 16px', textAlign:'right' }}>
          <div style={{ fontSize:14, fontWeight:700, color: pnl>=0 ? '#00C97A' : '#f04060' }}>
            {pnl>=0 ? '+' : ''}{pnl.toFixed(2)}
          </div>
          <div style={{ fontSize:10, color:'#556080', marginTop:2 }}>
            {trade.rr_actual ? `${parseFloat(trade.rr_actual).toFixed(2)}R` : '—'}
          </div>
        </td>
        <td style={{ padding:'12px 16px', textAlign:'center' }}>
          {trade.status === 'OPEN' ? (
            <span style={{ padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:700,
              background:'rgba(240,160,0,.12)', color:'#F0A500', animation:'pulse 2s infinite' }}>
              OPEN
            </span>
          ) : (
            <span style={{
              padding:'3px 10px', borderRadius:4, fontSize:10, fontWeight:700,
              background: win ? 'rgba(0,201,122,.12)' : 'rgba(240,64,96,.12)',
              color: win ? '#00C97A' : '#f04060',
            }}>
              {(trade.execution_outcome||'').includes('TRAIL') ? 'TRAIL' :
               win ? 'WIN' : 'LOSS'}
            </span>
          )}
        </td>
      </tr>

      {/* EXPANDED DETAIL */}
      {open && (
        <tr style={{ background:'#070b14' }}>
          <td colSpan={10} style={{ padding:0, borderBottom:'1px solid #1a1f30' }}>
            <div style={{ padding:'20px 24px' }}>

              {/* Inner tabs */}
              <div style={{ display:'flex', gap:0, marginBottom:20,
                borderBottom:'1px solid #1a1f30' }}>
                {(['overview','charts','notes'] as const).map(t => (
                  <button key={t} onClick={() => {
                setTab(t);
                if (t === 'charts' && !loadingSS) {
                  setLoadingSS(true);
                  api.get(`/api/v1/trades/${trade.id}/screenshots`)
                    .then(r => { setScreenshots(r.data || {}); })
                    .catch(() => { setScreenshots({}); })
                    .finally(() => setLoadingSS(false));
                }
              }} style={{
                    padding:'8px 20px', background:'none', border:'none',
                    borderBottom: tab===t ? '2px solid #00C97A' : '2px solid transparent',
                    color: tab===t ? '#E8ECF4' : '#556080',
                    fontSize:11, fontWeight:700, cursor:'pointer',
                    textTransform:'uppercase', letterSpacing:'.08em',
                    transition:'all .15s',
                  }}>{t}</button>
                ))}
              </div>

              {/* OVERVIEW TAB */}
              {tab === 'overview' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                  {/* Left - stats + AI analysis */}
                  <div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                      {[
                        { l:'Entry',    v: parseFloat(trade.entry_price||0).toFixed(dec) },
                        { l:'Exit',     v: trade.close_price ? parseFloat(trade.close_price).toFixed(dec) : '—' },
                        { l:'SL',       v: trade.sl ? parseFloat(trade.sl).toFixed(dec) : '—' },
                        { l:'TP',       v: trade.tp ? parseFloat(trade.tp).toFixed(dec) : '—' },
                        { l:'RR Target', v: trade.rr_target ? `${trade.rr_target}R` : '—' },
                        { l:'RR Actual', v: trade.rr_actual ? `${parseFloat(trade.rr_actual).toFixed(2)}R` : '—' },
                      ].map(s => (
                        <div key={s.l} style={{ background:'#0c0f1a',
                          borderRadius:6, padding:'10px 14px', border:'1px solid #1a1f30' }}>
                          <div style={{ fontSize:9, color:'#556080', textTransform:'uppercase',
                            letterSpacing:'.08em', marginBottom:4 }}>{s.l}</div>
                          <div style={{ fontSize:13, color:'#E8ECF4', fontWeight:600 }}>{s.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Entry Analysis */}
                    {(() => {
                      let ea: any = null;
                      try { ea = trade.entry_analysis ? JSON.parse(trade.entry_analysis) : null; } catch(e) {}
                      return ea ? (
                        <div style={{ marginBottom:12 }}>
                          {/* Entry Analysis - structural only */}
                          <div style={{ background:'rgba(0,201,122,.04)', border:'1px solid rgba(0,201,122,.15)',
                            borderRadius:6, padding:'14px 16px', marginBottom:8 }}>
                            <div style={{ fontSize:10, color:'#00C97A', textTransform:'uppercase' as const,
                              letterSpacing:'.08em', marginBottom:12 }}>📊 Entry Analysis</div>

                            {/* AI detected setup tags - confirmation UI */}
                            {ea.setup_tags && ea.setup_tags.length > 0 && (
                              <div style={{ marginBottom:12 }}>
                                <div style={{ fontSize:9, color:'#556080', marginBottom:6,
                                  textTransform:'uppercase' as const, letterSpacing:'.06em' }}>
                                  AI Detected Setup
                                </div>
                                <div style={{ display:'flex', flexWrap:'wrap' as const, gap:5 }}>
                                  {ea.setup_tags.map((tag:string, i:number) => (
                                    <span key={i} style={{ padding:'3px 10px', borderRadius:3,
                                      fontSize:10, fontWeight:700,
                                      background:'rgba(64,144,240,.1)', color:'#4090f0',
                                      border:'1px solid rgba(64,144,240,.2)' }}>{tag}</span>
                                  ))}
                                </div>
                                {/* Hidden setup detection */}
                                {(() => {
                                  const userTags = trade.tags || [];
                                  const userReversal = userTags.some((t:string) => ['Reversal','Reversal Attempt'].includes(t));
                                  const aiContinuation = ea.setup_tags.some((t:string) => ['Trend Continuation','BOS','Breakout'].includes(t));
                                  if (userReversal && aiContinuation) return (
                                    <div style={{ marginTop:8, padding:'7px 10px',
                                      background:'rgba(240,160,0,.06)',
                                      border:'1px solid rgba(240,160,0,.2)', borderRadius:4,
                                      fontSize:11, color:'#F0A500' }}>
                                      🔍 Interesting pattern: you tagged this as reversal — chart analysis detected continuation structure. This feeds your behavioural report.
                                    </div>
                                  );
                                  return null;
                                })()}
                              </div>
                            )}

                            {/* Structural observation - no trading guidance */}
                            {(ea.structural_observation || ea.entry_reasoning) && (
                              <div style={{ fontSize:12, color:'#8899b4', lineHeight:1.7, marginBottom:8 }}>
                                {ea.structural_observation || ea.entry_reasoning}
                              </div>
                            )}
                            {(ea.key_zone || ea.key_level) && (
                              <div style={{ fontSize:11, color:'#F0A500', marginBottom:4 }}>
                                📍 Key structural zone identified
                              </div>
                            )}
                            {ea.trendline_touches > 0 && (
                              <div style={{ fontSize:11, color:'#4090f0', marginBottom:4 }}>
                                📐 {ea.trendline_type} trendline — {ea.trendline_touches} touches detected
                              </div>
                            )}
                            {ea.news_risk && (
                              <div style={{ fontSize:11, color:'#f04060' }}>
                                ⚠ High impact news event detected near entry time
                              </div>
                            )}
                          </div>

                          {/* Scenario Analysis */}
                          {(ea.sim_tp_pnl !== undefined || ea.sim_sl_pnl !== undefined) && (
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                              <div style={{ background:'rgba(0,201,122,.04)', border:'1px solid rgba(0,201,122,.15)',
                                borderRadius:5, padding:'10px 14px' }}>
                                <div style={{ fontSize:9, color:'#556080', textTransform:'uppercase' as const,
                                  letterSpacing:'.06em', marginBottom:4 }}>Scenario: All reach target zone</div>
                                <div style={{ fontSize:16, fontWeight:700, color:'#00C97A' }}>
                                  {ea.sim_tp_pnl >= 0 ? '+' : ''}{ea.sim_tp_pnl?.toFixed(2) || '—'}
                                </div>
                              </div>
                              <div style={{ background:'rgba(240,64,96,.04)', border:'1px solid rgba(240,64,96,.15)',
                                borderRadius:5, padding:'10px 14px' }}>
                                <div style={{ fontSize:9, color:'#556080', textTransform:'uppercase' as const,
                                  letterSpacing:'.06em', marginBottom:4 }}>Scenario: All reach stop zone</div>
                                <div style={{ fontSize:16, fontWeight:700, color:'#f04060' }}>
                                  {ea.sim_sl_pnl?.toFixed(2) || '—'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        trade.status === 'OPEN' ? (
                          <div style={{ background:'rgba(64,144,240,.05)', border:'1px solid rgba(64,144,240,.15)',
                            borderRadius:6, padding:'12px 14px', marginBottom:10, fontSize:12, color:'#4090f0' }}>
                            ⏳ AI analysis processing... Check back shortly.
                          </div>
                        ) : (() => {
                          // Check if analysis was skipped
                          let parsed: any = null;
                          try { parsed = trade.entry_analysis ? JSON.parse(trade.entry_analysis) : null; } catch(e) {}
                          if (parsed?.skipped) return (
                            <div style={{ background:'rgba(85,96,128,.05)', border:'1px solid rgba(85,96,128,.15)',
                              borderRadius:6, padding:'12px 14px', marginBottom:10, fontSize:11, color:'#556080' }}>
                              📸 Screenshots saved · {parsed.reason}
                            </div>
                          );
                          return null;
                        })()
                      );
                    })()}

                    {/* Exit Analysis */}
                    {(() => {
                      let xa: any = null;
                      try { xa = trade.exit_analysis ? JSON.parse(trade.exit_analysis) : null; } catch(e) {}
                      return xa ? (
                        <div style={{ background:'rgba(144,96,240,.04)',
                          border:'1px solid rgba(144,96,240,.15)',
                          borderRadius:6, padding:'14px 16px', marginBottom:10 }}>
                          <div style={{ fontSize:10, color:'#9060f0', textTransform:'uppercase' as const,
                            letterSpacing:'.08em', marginBottom:10, display:'flex',
                            justifyContent:'space-between', alignItems:'center' }}>
                            <span>🧠 Exit Analysis</span>
                          </div>
                          <div style={{ fontSize:12, color:'#8899b4', lineHeight:1.7, marginBottom:8 }}>{xa.overall_analysis}</div>
                          {xa.what_went_right && <div style={{ fontSize:11, color:'#00C97A', marginBottom:4 }}>✓ {xa.what_went_right}</div>}
                          {xa.what_went_wrong && xa.what_went_wrong !== 'Nothing significant' && (
                            <div style={{ fontSize:11, color:'#f04060', marginBottom:4 }}>✗ {xa.what_went_wrong}</div>
                          )}
                          {xa.lesson && <div style={{ fontSize:11, color:'#F0A500', marginTop:4 }}>💡 {xa.lesson}</div>}
                        </div>
                      ) : (
                        trade.status === 'CLOSED' ? (
                          <div style={{ padding:'10px', background:'rgba(144,96,240,.05)',
                            border:'1px solid rgba(144,96,240,.1)', borderRadius:6,
                            color:'#556080', fontSize:11, textAlign:'center' as const, marginBottom:10 }}>
                            🧠 Exit analysis runs automatically after trade closes
                          </div>
                        ) : null
                      );
                    })()}

                    {/* Post-exit */}
                    {trade.post_exit_tracked && exitQ && (
                      <div style={{ background:`${exitColor}08`,
                        border:`1px solid ${exitColor}25`,
                        borderRadius:6, padding:'12px 16px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', marginBottom:10 }}>
                          <div style={{ fontSize:10, color:exitColor, textTransform:'uppercase',
                            letterSpacing:'.08em', fontWeight:700 }}>Exit Quality</div>
                          <span style={{ padding:'2px 10px', borderRadius:4, fontSize:10,
                            fontWeight:700, background:`${exitColor}15`, color:exitColor }}>
                            {exitQ}
                          </span>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                          <div style={{ background:'#0c0f1a', borderRadius:4, padding:'8px 10px' }}>
                            <div style={{ fontSize:9, color:'#556080', marginBottom:3 }}>60min High</div>
                            <div style={{ fontSize:13, color:'#00C97A', fontWeight:600 }}>
                              {parseFloat(trade.post_exit_high||0).toFixed(dec)}
                            </div>
                          </div>
                          <div style={{ background:'#0c0f1a', borderRadius:4, padding:'8px 10px' }}>
                            <div style={{ fontSize:9, color:'#556080', marginBottom:3 }}>60min Low</div>
                            <div style={{ fontSize:13, color:'#f04060', fontWeight:600 }}>
                              {parseFloat(trade.post_exit_low||0).toFixed(dec)}
                            </div>
                          </div>
                        </div>
                        {exitQ === 'EARLY' && (
                          <div style={{ fontSize:11, color:'#F0A500', marginTop:8 }}>
                            ⚠ Price continued in your favour — consider trailing stops
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right - tags */}
                  <div>
                    <div style={{ marginBottom:12 }}>

                      {/* TagPicker — one clean section each, no duplication */}
                      <TagPicker selected={tags} onChange={setTags} tradeId={trade.id} />

                      {/* System tags — read only, shown below picker */}
                      {(() => {
                        const entryT = (trade.entry_tags || []) as string[];
                        const exitT  = (trade.exit_tags  || []) as string[];
                        const RISK   = ['No Risk','Balanced Risk','Elevated Risk','Aggressive Risk'];
                        const riskT  = (trade.tags || []).filter((t:string) => RISK.includes(t));
                        if (!entryT.length && !exitT.length && !riskT.length) return null;
                        const Pill = ({ label, color }: any) => (
                          <span style={{ padding:'2px 9px', borderRadius:3, fontSize:10,
                            fontWeight:700, background:`${color}15`, color,
                            border:`1px solid ${color}25` }}>{label}</span>
                        );
                        const SysRow = ({ label, items, color }: any) => items.length === 0 ? null : (
                          <div style={{ marginBottom:8 }}>
                            <div style={{ fontSize:9, color:'#3a4560', textTransform:'uppercase' as const,
                              letterSpacing:'.07em', marginBottom:4, fontWeight:600 }}>{label}</div>
                            <div style={{ display:'flex', flexWrap:'wrap' as const, gap:5 }}>
                              {items.map((t:string, i:number) => <Pill key={i} label={t} color={color} />)}
                            </div>
                          </div>
                        );
                        return (
                          <div style={{ marginTop:14, paddingTop:12, borderTop:'1px solid #111626' }}>
                            <div style={{ fontSize:9, color:'#3a4560', textTransform:'uppercase' as const,
                              letterSpacing:'.07em', marginBottom:8, fontWeight:600 }}>
                              System Tags — read only
                            </div>
                            <SysRow label="Entry Tags" items={entryT} color="#9060f0" />
                            <SysRow label="Exit Tags"  items={exitT}  color="#00C97A" />
                            <SysRow label="Risk Tags"  items={riskT}  color="#f04060" />
                          </div>
                        );
                      })()}
                    </div>
                    <button onClick={save} disabled={saving} style={{
                      padding:'9px 20px', background:'#00C97A', border:'none',
                      borderRadius:6, color:'#000', fontSize:11, fontWeight:700,
                      cursor:'pointer', letterSpacing:'.06em', marginTop:8,
                    }}>
                      {saving ? 'Saving...' : 'Save Tags'}
                    </button>
                  </div>
                </div>
              )}

              {/* CHARTS TAB */}
              {tab === 'charts' && (
                <div>
                  {loadingSS ? (
                <div style={{ textAlign:'center', color:'#556080', padding:'40px 0', fontSize:12 }}>
                  Loading charts...
                </div>
              ) : screenshots === null ? (
                <div style={{ textAlign:'center', color:'#556080', padding:'40px 0', fontSize:12 }}>
                  Click Charts tab to load
                </div>
              ) : !screenshots?.screenshot_entry && !screenshots?.screenshot_h1_entry ? (
                    <div style={{ textAlign:'center', color:'#556080', padding:'40px 0' }}>
                      <div style={{ fontSize:24, marginBottom:8 }}>
                        {trade.status === 'OPEN' ? '⏳' : '📸'}
                      </div>
                      <div>{trade.status === 'OPEN' ? 'Entry chart uploading...' : 'No charts captured yet'}</div>
                      <div style={{ fontSize:11, marginTop:4 }}>
                        {trade.status === 'OPEN'
                          ? 'Entry chart uploads within 30 seconds of trade open'
                          : 'Charts are captured automatically when EA is active'}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Entry charts */}
                      {(screenshots?.screenshot_entry || screenshots?.screenshot_h1_entry) && (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ fontSize:11, color:'#00C97A', fontWeight:700,
                            textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                            Entry
                          </div>
                          <div style={{ display:'grid',
                            gridTemplateColumns: screenshots?.screenshot_entry && screenshots?.screenshot_h1_entry
                              ? '1fr 1fr' : '1fr', gap:12 }}>
                            {screenshots?.screenshot_entry && (
                              <div>
                                <div style={{ fontSize:9, color:'#556080', marginBottom:6 }}>M15</div>
                                <img
                                  src={screenshots.screenshot_entry.startsWith('data:')
                                    ? screenshots.screenshot_entry
                                    : `data:image/png;base64,${screenshots.screenshot_entry}`}
                                  alt="M15 Entry"
                                  style={{ width:'100%', borderRadius:6,
                                    border:'1px solid #1a1f30', display:'block' }}/>
                              </div>
                            )}
                            {screenshots?.screenshot_h1_entry && (
                              <div>
                                <div style={{ fontSize:9, color:'#556080', marginBottom:6 }}>H1</div>
                                <img
                                  src={screenshots.screenshot_h1_entry.startsWith('data:')
                                    ? screenshots.screenshot_h1_entry
                                    : `data:image/png;base64,${screenshots.screenshot_h1_entry}`}
                                  alt="H1 Entry"
                                  style={{ width:'100%', borderRadius:6,
                                    border:'1px solid #1a1f30', display:'block' }}/>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Exit charts */}
                      {(screenshots?.screenshot_exit || screenshots?.screenshot_h1_exit) && (
                        <div>
                          <div style={{ fontSize:11, color:'#f04060', fontWeight:700,
                            textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                            Exit
                          </div>
                          <div style={{ display:'grid',
                            gridTemplateColumns: screenshots?.screenshot_exit && screenshots?.screenshot_h1_exit
                              ? '1fr 1fr' : '1fr', gap:12 }}>
                            {screenshots?.screenshot_exit && (
                              <div>
                                <div style={{ fontSize:9, color:'#556080', marginBottom:6 }}>M15</div>
                                <img
                                  src={screenshots.screenshot_exit.startsWith('data:')
                                    ? screenshots.screenshot_exit
                                    : `data:image/png;base64,${screenshots.screenshot_exit}`}
                                  alt="M15 Exit"
                                  style={{ width:'100%', borderRadius:6,
                                    border:'1px solid #1a1f30', display:'block' }}/>
                              </div>
                            )}
                            {screenshots?.screenshot_h1_exit && (
                              <div>
                                <div style={{ fontSize:9, color:'#556080', marginBottom:6 }}>H1</div>
                                <img
                                  src={screenshots.screenshot_h1_exit.startsWith('data:')
                                    ? screenshots.screenshot_h1_exit
                                    : `data:image/png;base64,${screenshots.screenshot_h1_exit}`}
                                  alt="H1 Exit"
                                  style={{ width:'100%', borderRadius:6,
                                    border:'1px solid #1a1f30', display:'block' }}/>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* NOTES TAB */}
              {tab === 'notes' && (
                <div>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="What was your reasoning? What would you do differently? Any market observations..."
                    style={{
                      width:'100%', minHeight:160, padding:'12px 14px',
                      background:'#0c0f1a', border:'1px solid #1a1f30',
                      borderRadius:6, color:'#E8ECF4', fontSize:13,
                      fontFamily:'inherit', resize:'vertical', outline:'none',
                      lineHeight:1.7,
                    }}/>
                  <button onClick={save} disabled={saving} style={{
                    marginTop:12, padding:'9px 24px', background:'#00C97A',
                    border:'none', borderRadius:6, color:'#000',
                    fontSize:11, fontWeight:700, cursor:'pointer',
                    letterSpacing:'.06em',
                  }}>
                    {saving ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── MAIN JOURNAL ──
export default function Journal() {
  const [trades,        setTrades]        = useState<any[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [filterSymbol,  setFilterSymbol]  = useState('');
  const [filterTag,     setFilterTag]     = useState('');
  const [filterResult,  setFilterResult]  = useState('all');
  const getWeekStart = () => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Mon
    d.setDate(diff);
    return d.toISOString().slice(0,10);
  };
  const defaultFrom = getWeekStart();
  const defaultTo   = new Date().toISOString().slice(0,10);
  const [filterFrom, setFilterFrom] = useState(defaultFrom);
  const [filterTo,   setFilterTo]   = useState(defaultTo);

  const load = async (force=false) => {
    const cacheKey = `journal_trades_all`;
    // Use cache for speed unless forced
    if (!force) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          setTrades(JSON.parse(cached));
          setLoading(false);
          // Refresh in background
          loadFresh(cacheKey);
          return;
        } catch(e) {}
      }
    }
    setLoading(true);
    await loadFresh(cacheKey);
    setLoading(false);
  };

  const loadFresh = async (cacheKey: string) => {
    try {
      const [closedR, openR] = await Promise.all([
        getTrades({ status:'CLOSED', period:'all' }).catch(() => ({ data:[] })),
        getTrades({ status:'OPEN' }).catch(() => ({ data:[] })),
      ]);
      const all = [...(openR.data||[]), ...(closedR.data||[])];
      setTrades(all);
      sessionStorage.setItem(cacheKey, JSON.stringify(all));
    } catch(e) {}
  };

  useEffect(() => { setLoading(true); load(); }, []); // eslint-disable-line

  const filtered = trades.filter(t => {
    if (filterSymbol && t.symbol !== filterSymbol) return false;
    if (filterTag && !(t.tags||[]).includes(filterTag)) return false;
    if (filterResult === 'win'  && !(t.execution_outcome||'').startsWith('WIN'))  return false;
    if (filterResult === 'loss' && !(t.execution_outcome||'').startsWith('LOSS')) return false;
    const tradeDate = (t.close_time || t.open_time || '').slice(0,10);
    if (filterFrom && tradeDate < filterFrom) return false;
    if (filterTo   && tradeDate > filterTo)   return false;
    return true;
  });

  const symbols = Array.from(new Set(trades.map((t:any) => t.symbol as string)));
  const allTags = Array.from(new Set(trades.flatMap((t:any) => (t.tags||[]) as string[])));
  const wins    = filtered.filter(t => (t.execution_outcome||'').startsWith('WIN')).length;
  const losses  = filtered.filter(t => (t.execution_outcome||'').startsWith('LOSS')).length;
  const netPnl  = filtered.reduce((s,t) => s + parseFloat(t.net_pnl||0), 0);
  const avgRR   = filtered.filter(t=>t.rr_actual).length > 0
    ? filtered.reduce((s,t) => s+parseFloat(t.rr_actual||0),0) / filtered.filter(t=>t.rr_actual).length
    : 0;
  const winRate = filtered.length > 0 ? Math.round(wins/filtered.length*100) : 0;

  const updateTrade = (u: any) => setTrades(trades.map(t => t.id===u.id ? u : t));

  return (
    <div style={{ padding:'24px 28px', maxWidth:1300, margin:'0 auto' }}>
      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
        <StatCard label="Net P&L" value={`${netPnl>=0?'+':''}${netPnl.toFixed(2)}`}
          color={netPnl>=0?'#00C97A':'#f04060'} />
        <StatCard label="Trades" value={filtered.length} sub={`${wins}W · ${losses}L`} />
        <StatCard label="Win Rate" value={`${winRate}%`}
          color={winRate>=50?'#00C97A':winRate>=40?'#F0A500':'#f04060'} />
        <StatCard label="Avg RR" value={avgRR>0?`${avgRR.toFixed(2)}R`:'—'}
          color={avgRR>=1?'#00C97A':'#F0A500'} />
        <StatCard label="Best Trade"
          value={filtered.length ? `+${Math.max(...filtered.map(t=>parseFloat(t.net_pnl||0))).toFixed(2)}` : '—'}
          color="#00C97A" />
      </div>

      {/* Filters */}
      <div style={{ display:'flex', alignItems:'center', gap:10,
        marginBottom:16, flexWrap:'wrap' as const }}>
        {/* From date */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, color:'#556080' }}>From</span>
          <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)}
            style={{ padding:'7px 12px', background:'#0c0f1a', border:'1px solid #252d42',
              borderRadius:6, color:'#E8ECF4', fontSize:11, fontFamily:'inherit',
              colorScheme:'dark' as any }}/>
        </div>
        {/* To date */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:11, color:'#556080' }}>To</span>
          <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)}
            style={{ padding:'7px 12px', background:'#0c0f1a', border:'1px solid #252d42',
              borderRadius:6, color:'#E8ECF4', fontSize:11, fontFamily:'inherit',
              colorScheme:'dark' as any }}/>
        </div>
        <select value={filterResult} onChange={e=>setFilterResult(e.target.value)} style={{
          padding:'7px 12px', background:'#0c0f1a', border:'1px solid #252d42',
          borderRadius:6, color:'#E8ECF4', fontSize:11, fontFamily:'inherit' }}>
          <option value="all">All Results</option>
          <option value="win">Wins Only</option>
          <option value="loss">Losses Only</option>
        </select>
        <select value={filterSymbol} onChange={e=>setFilterSymbol(e.target.value)} style={{
          padding:'7px 12px', background:'#0c0f1a', border:'1px solid #252d42',
          borderRadius:6, color:'#E8ECF4', fontSize:11, fontFamily:'inherit' }}>
          <option value="">All Symbols</option>
          {symbols.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        {allTags.length > 0 && (
          <select value={filterTag} onChange={e=>setFilterTag(e.target.value)} style={{
            padding:'7px 12px', background:'#0c0f1a', border:'1px solid #252d42',
            borderRadius:6, color:'#E8ECF4', fontSize:11, fontFamily:'inherit' }}>
            <option value="">All Tags</option>
            {allTags.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {(filterSymbol||filterTag||filterResult!=='all') && (
          <button onClick={()=>{
            setFilterSymbol('');setFilterTag('');setFilterResult('all');
            setFilterFrom(defaultFrom);setFilterTo('');
          }} style={{
            padding:'7px 12px', background:'transparent',
            border:'1px solid #252d42', borderRadius:6,
            color:'#556080', fontSize:11, cursor:'pointer' }}>
            Clear
          </button>
        )}
        <div style={{ marginLeft:'auto', color:'#556080', fontSize:11 }}>
          {filtered.length} trades
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'#0c0f1a', border:'1px solid #1a1f30', borderRadius:8, overflow:'hidden' }}>
        {loading ? (
          <div style={{ textAlign:'center', color:'#556080', padding:'48px 0' }}>
            Loading trades...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', color:'#556080', padding:'60px 0' }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📓</div>
            <div style={{ fontSize:14, marginBottom:6 }}>No trades found</div>
            <div style={{ fontSize:12 }}>Connect your MT5 EA to start journalling automatically</div>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #1a1f30', background:'#070b14' }}>
                {['Date','Symbol','Dir','Lot','Entry','Exit','Duration','Tags','P&L','Result'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left',
                    fontSize:9, fontWeight:700, color:'#556080',
                    textTransform:'uppercase', letterSpacing:'.1em',
                    whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(trade => (
                <TradeRow key={trade.id} trade={trade} onUpdate={updateTrade} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
